import React, { useState } from "react"
import { Modal, ModalHeader, ModalFooter, Button, ModalBody, Input, CustomInput, FormGroup, Label } from "reactstrap"
import { FIGHT_CATEGORY_ID } from 'web/data/general/id'
import { GuiData } from 'web/data/gui-data'
import { useFocusOnMount } from 'web/hooks/useFocusOnMount'
import { useKeyShortcut } from 'web/hooks/useKeyShortcut'
import { createGroupedOptions } from 'web/utils/jsx-utils'
import { dataTest } from 'web/utils/test-utils'
import { joinLines, emptyStringAsUndefined } from 'web/utils/utils'
import { ModalContainerProps } from "./_ModalContainer"

export type CreateOutlineModalProps = {
    onConfirm: (
        outlineCategoryId: string,
        outlineName: string | undefined
    ) => void
}

export const CreateOutlineModal: React.FC<CreateOutlineModalProps & ModalContainerProps> = ({
    onConfirm, closeSelf
}) => {

    const [outlineName, setOutlineName] = useState<string>('Untitled')
    const [fightCategory, setFightCategory] = useState<string>(FIGHT_CATEGORY_ID.CUSTOM.Uncategorized)

    const fightCategoryOptions = createGroupedOptions({
        optionGroups: GuiData.FightCategoryOptions,
        onlyIncludeSelectable: true
    })

    const focusRef = useFocusOnMount({ selectText: true })

    const doSave = () => {
        onConfirm(fightCategory, emptyStringAsUndefined(outlineName))
        closeSelf()
    }

    useKeyShortcut({
        key: "s",
        modifier: "ctrl",
        callback: doSave,
    })

    return (
        <Modal
            isOpen
            fade={false}
            toggle={closeSelf}
            backdrop={"static"}
        >

            <ModalHeader>
                {`Create new outline`}
            </ModalHeader>

            <ModalBody className="p-3 m-0 bg-light">

                <FormGroup className="mb-0 small flex-grow-1 mb-3">
                    <Label>{"Fight category"}</Label>
                    <CustomInput
                        innerRef={focusRef}
                        data-test={dataTest('edit-fight-category-select')}
                        id="fight-category-select"
                        bsSize="sm"
                        type="select"
                        onChange={e => setFightCategory(e.target.value)}
                        value={fightCategory}
                    >
                        {fightCategoryOptions}
                    </CustomInput>
                </FormGroup>

                <div className="small pb-2">
                    {joinLines([
                        'Pick a new name for the outline. A common pattern is to describe',
                        'which difficulty it is for, or some detail about the outline.',
                        'For example, "Heroic", "Mythic" or "Speedkill".'
                    ])}
                </div>

                <Input
                    bsSize="sm"
                    type="text"
                    value={outlineName}
                    onChange={e => setOutlineName(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" ? doSave() : null}
                    data-test={"prompt-input"}
                />
            </ModalBody>

            <ModalFooter className="d-flex justify-content-end space-mx-2" >
                <Button
                    size="sm"
                    onClick={doSave}
                    color={'success'}
                >
                    {"Confirm"}
                </Button>

                <Button
                    outline
                    size="sm"
                    onClick={closeSelf}
                    title={"Cancel (esc)"}
                >
                    {"Cancel"}
                </Button>
            </ModalFooter>
        </Modal>
    )
}
